<template>
  <div>
    <h2 class="text-center text-[28px] font-semibold">短信登录</h2>
    <p class="mt-[4px] text-center text-base">请使用手机验证码登录</p>
    <Form class="my-6 login-message-form" @submit.prevent="onClickLoginOrRegister">
      <FormItem>
        <Input v-model.trim="phone" placeholder="请输入手机号" @on-keydown="onInputPhoneNumber" @on-change="onPhoneNumberChange">
          <template #prepend>
            <Select v-model="country" @on-change="error = ''">
              <Option v-for="country in Countries" :key="country.code" :value="country.code" :label="`+${country.code}`">
                <span>{{ country.name_cn }} +{{ country.code }}</span>
              </Option>
            </Select>
          </template>
        </Input>
      </FormItem>
      <FormItem>
        <div id="login-captcha-container">
          <Captcha
            ref="captchaInstance"
            container="#login-captcha-container"
            @success="onCaptchaSuccess"
            @fail="onCaptchaFail"
            @error="onCaptchaError"
          ></Captcha>
        </div>
      </FormItem>
      <FormItem v-if="sent">
        <Input v-model="code" placeholder="请输入 6 位短信验证码">
          <template #append>
            <div v-if="seconds > 0" class="px-2 text-disabled select-none">{{ seconds }} 秒后重新发送</div>
            <div v-else class="bg-primary-main text-white leading-[48px] cursor-pointer select-none" @click="onClickSendMessage">发送验证码</div>
          </template>
        </Input>
      </FormItem>
      <FormItem v-if="error" class="min-h-0">
        <Alert type="error" show-icon class="m-0">{{ error }}</Alert>
      </FormItem>
      <FormItem>
        <Button html-type="submit" type="primary" size="large" long class="h-12 rounded-md" :loading="loading">登录&nbsp;/&nbsp;注册</Button>
      </FormItem>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import axios from 'axios';
import getErrorMessage from '@/utils/get-error-message';
import getAWSCTokens from '@/utils/get-awsc-tokens';
import Countries from '@/constants/country-list';
import Captcha from '@/components/shared/captcha.vue';
import useLoginCheck from '@/composables/login-check.ts';
import trackEvent from '@/utils/track-event';
import { debounce } from 'lodash-es';

type CaptchaExpose = {
  init: (forceRecreate?: boolean) => void
};

type CaptchaVerifyData = {
  captchaVerifyParam: string
  sceneId: string
};

const country = ref('86');
const phone = ref('');
const code = ref('');
const sent = ref(false);
const error = ref('');
const captchaInstance = ref<CaptchaExpose | null>(null);
const captchaData = ref<CaptchaVerifyData | null>(null);
const accountStatus = ref('');

const seconds = ref(60);
const timer = ref<ReturnType<typeof setInterval> | 0>(0);
const loading = ref(false);

const createSession = async () => {
  try {
    await axios.post('/api/auth/session');
  } catch (e) {
    error.value = '创建 Session 失败，请稍后重试';
  }
};

const refreshCaptcha = (forceRecreate = false) => {
  captchaInstance.value?.init(forceRecreate);
};

const onCaptchaSuccess = (data: CaptchaVerifyData) => {
  captchaData.value = data;
  error.value = '';
  onClickSendMessage();
};

const onCaptchaFail = () => {
  captchaData.value = null;
  error.value = '请先完成滑动验证';
};

const onCaptchaError = (errorCode: unknown) => {
  captchaData.value = null;
  if (errorCode === 'missing-scene-id' || errorCode === 'captcha-sdk-not-ready') {
    error.value = '验证码加载失败，请刷新后重试';
    return;
  }
  error.value = '验证码服务异常，请稍后重试';
};

const isLegacyCaptchaSchemaError = (err: unknown) => {
  const message = getErrorMessage(err).toLowerCase();
  const hasUnknownField = message.includes('unknown field') || message.includes('未知字段');
  const hasCaptchaField = message.includes('captchaverifyparam') || message.includes('sceneid');
  return hasUnknownField && hasCaptchaField;
};

const sendRegisterSmsCodeWithCompatibility = async (basePayload: { phone: string; countryCode: string }) => {
  if (!captchaData.value) {
    throw new Error('captcha-data-missing');
  }

  const enhancedPayload = {
    ...basePayload,
    captchaVerifyParam: captchaData.value.captchaVerifyParam,
    sceneId: captchaData.value.sceneId,
    scene: captchaData.value.sceneId,
  };

  try {
    return await axios.post('/api/auth/sendRegisterSmsCode', enhancedPayload);
  } catch (err) {
    if (!isLegacyCaptchaSchemaError(err)) {
      throw err;
    }
    return await axios.post('/api/auth/sendRegisterSmsCode', basePayload);
  }
};

const onClickSendMessage = async () => {
  if (seconds.value > 0 && sent.value) {
    return;
  }

  if (!captchaData.value) {
    error.value = '请先完成验证';
    return;
  }

  if (!phone.value.match(/^\d{7,}$/)) {
    refreshCaptcha(true);
    captchaData.value = null;
    error.value = '请输入有效的手机号';
    return;
  }

  try {
    const payloadBase = {
      phone: phone.value,
      countryCode: country.value,
    };
    const response = await sendRegisterSmsCodeWithCompatibility(payloadBase);
    const responseData = response.data as { status?: string } | undefined;
    accountStatus.value = responseData?.status || '';
    sent.value = true;
    error.value = '';
    seconds.value = 60;

    if (timer.value) {
      clearInterval(timer.value);
    }

    timer.value = setInterval(() => {
      if (seconds.value <= 0) {
        refreshCaptcha(true);
        clearInterval(timer.value);
        timer.value = 0;
        sent.value = false;
        captchaData.value = null;
        error.value = '';
        return;
      }
      seconds.value = seconds.value - 1;
    }, 1000);
  } catch (err) {
    refreshCaptcha(true);
    sent.value = false;
    captchaData.value = null;
    seconds.value = 0;
    error.value = getErrorMessage(err);
  }
};

const onClickLoginOrRegister = async () => {
  if (!phone.value) {
    error.value = '请输入手机号';
    return;
  }
  if (!sent.value) {
    error.value = '请先获取验证码';
    return;
  }
  if (!code.value) {
    error.value = '请输入验证码';
    return;
  }
  const { webUmidToken, uaToken } = getAWSCTokens();
  const payload: { [key: string]: string } = {
    webUmidToken,
    uaToken,
    code: code.value,
  };
  try {
    if (accountStatus.value === 'ACTIVE') {
      // 登录
      trackEvent('click_register', 'phone', 'community');
      login(payload)
    }
    if (accountStatus.value === 'NOT_FOUND') {
      // 注册
      trackEvent('click_register', 'phone', 'community');
      register(payload);
    }
    if (accountStatus.value === 'BANNED') {
      error.value = '此账号已被封禁';
    }
  } catch (err) {
    error.value = getErrorMessage(err);
  }
};

const onInputPhoneNumber = (event: KeyboardEvent) => {
  const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Control', 'Meta', 'Shift', 'Alt', 'Enter'];
  const isCtrlCmd = event.ctrlKey || event.metaKey;
  const isAllowedKey = allowedKeys.includes(event.key);

  if (isCtrlCmd || isAllowedKey) {
    return;
  }

  if (!/^\d$/.test(event.key)) {
    event.preventDefault();
  }
};

const onPhoneNumberChange = () => {
  phone.value = phone.value.replace(/\D/g, '');
};

onMounted(() => {
  createSession();
});

onUnmounted(() => {
  if (timer.value) {
    clearInterval(timer.value);
  }
});

const login = debounce(async (payload: { [key: string]: string }) => {
  try {
    loading.value = true;
    const { data } = await axios.post('/api/auth/loginBySmsCode', payload);
    if (data?.code === 4) {
      error.value = data?.message || '';
      return;
    }
    useLoginCheck();
  } finally {
    loading.value = false;
  }
}, 300);

const register = debounce(async (payload: { [key: string]: string }) => {
  try {
    loading.value = true;
    await axios.post('/api/users/registerByPhone', payload);
    useLoginCheck();
  } finally {
    loading.value = false;
  }
}, 300);

</script>

<style lang="scss">
.login-message-form {
  .ivu-form-item {
    min-height: 48px;
    margin-bottom: 16px;
  }
  .ivu-input-wrapper {
    input {
      height: 48px !important;
    }
  }
  .ivu-input-group-with-prepend {
    .ivu-input-group-prepend {
      width: 80px;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
    input {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }
  .ivu-input-group-with-append {
    .ivu-input-group-append {
      min-width: 90px;
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
      overflow: hidden;
      padding: 0px;
      height: 48px;
    }
    input {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
  }
}
</style>
